import React from "react";
import { MessageCircle, CalendarCheck, PencilRuler, Receipt, Truck, KeyRound, ChevronRight } from "lucide-react";

const STEPS = [
    { n: "01", title: "Meet a Specialist", desc: "Tell us about your space, goals, size, timeline and budget. We'll understand your requirement and guide you on the right next step.", icon: <MessageCircle className="h-9 w-9" /> },
    { n: "02", title: "Book Design Slot", desc: "Pay AED 5,000 as a design fee to confirm your project slot. This covers the design direction, planning and initial concept work.", icon: <CalendarCheck className="h-9 w-9" /> },
    { n: "03", title: "Design Confirmation", desc: "We prepare the design direction, layout, material direction and estimate. Once the design and scope are approved, the project moves to execution.", icon: <PencilRuler className="h-9 w-9" /> },
    { n: "04", title: "60% Project Advance", desc: "After design approval, pay 60% of the total project value to begin procurement, production, vendor coordination and execution planning.", icon: <Receipt className="h-9 w-9" /> },
    { n: "05", title: "Balance Before Installation", desc: "Once items are ready and delivered to site, the remaining 40% balance is cleared before installation, final fixing and finishing begins.", icon: <Truck className="h-9 w-9" /> },
    { n: "06", title: "Install & Move In", desc: "Our team completes installation, finishing, quality checks and handover so your space is ready to use.", icon: <KeyRound className="h-9 w-9" /> },
];

export default function Process() {
    return (
        <section className="bg-[#FAFAF5] pt-12 pb-20 sm:pt-16 sm:pb-28" data-testid="process-section">
            <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end fade-up">
                    <h2 className="lg:col-span-8 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
                        From first call to final handover — with clear milestones.
                    </h2>
                    <p className="lg:col-span-4 text-sm text-black/55 max-w-md lg:text-right">
                        A simple, transparent process for Dubai businesses and homeowners — from design commitment to execution, installation and move-in.
                    </p>
                </div>

                {/* Timeline */}
                <div className="mt-16">
                    {/* Desktop */}
                    <div className="hidden lg:flex items-start justify-between gap-2 relative">
                        {STEPS.map((s, i) => (
                            <React.Fragment key={s.n}>
                                <div className="flex-1 flex flex-col items-center text-center fade-up">
                                    <div className="relative">
                                        <span className="absolute -top-2 left-1/2 -translate-x-1/2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-[#CCFF00] text-black text-[12px] font-bold">
                                            {s.n}
                                        </span>
                                        <div className="mt-3 flex h-32 w-32 items-center justify-center rounded-full bg-white shadow-[0_8px_24px_rgba(0,0,0,0.05)] border border-black/5">
                                            <div className="text-black">{s.icon}</div>
                                        </div>
                                    </div>
                                    <h3 className="mt-6 font-display text-[17px] font-bold">{s.title}</h3>
                                    <p className="mt-3 max-w-[13rem] text-[13px] text-black/60 leading-relaxed">{s.desc}</p>
                                </div>
                                {i < STEPS.length - 1 && (
                                    <div className="self-start mt-14 flex items-center">
                                        <div className="dashed-line w-5" />
                                        <ChevronRight className="h-5 w-5 text-black/40" />
                                        <div className="dashed-line w-5" />
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Mobile */}
                    <div className="lg:hidden grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {STEPS.map((s) => (
                            <div key={s.n} className="flex gap-4 fade-up">
                                <div className="relative shrink-0">
                                    <span className="absolute -top-1 -left-1 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-[#CCFF00] text-black text-[11px] font-bold">
                                        {s.n}
                                    </span>
                                    <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white border border-black/10">
                                        {s.icon}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="font-display text-base font-bold">{s.title}</h3>
                                    <p className="mt-2 text-sm text-black/60">{s.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Milestone note */}
                    <p className="mt-12 text-center text-xs text-black/50 italic max-w-2xl mx-auto fade-up">
                        Payment milestones may vary depending on project size, scope, procurement requirements and agreed contract terms.
                    </p>
                </div>
            </div>
        </section>
    );
}
